import { Autocomplete, Button, InputAdornment, TextField } from "@mui/material"
import { SearchFor } from "event-definitions"
import Iconify from "minimals-template/components/Iconify"
import { useState } from "react"
import { setFromValueParam } from "lib/setFromEvent"
import { clearSearchTerm } from "routes/facilities/plugins/helpers/use-clean-debounced-value"
import { useDemand } from "lib/authorization/use-demand"
import { useSearchParam } from "lib/@hooks/use-search-param"
import { useLocation } from "react-router-dom"
import { navigate } from "lib/routes/navigate"
import { SCHEDULE } from "library/constants.mjs"
import { handleUnlicensedContent } from "routes/schedule/lib/handle-unlicensed-content"
import {
    getFacilityProfileScheduleRoute,
    getRegimeRoute,
} from "routes/facilities/plugins/maintenance-profile-tab/facility-profile-routes"
import { useTreeItem } from "routes/schedule/lib/useTreeItem"
import { useDebouncedValue } from "lib/@hooks/useDebouncedValue"
import {
    getShareLinkSearchPlaceholder,
    getShareLinkSearchRegimeId,
} from "routes/schedule/plugins/share-link-search/share-link-search-placeholder"
import { useRegimeSearchMatches } from "routes/schedule/plugins/share-link-search/use-regime-search-matches"
import { ShareLinkSearchResult } from "routes/schedule/plugins/share-link-search/share-link-search-result"

export function ShareLinkSearchBox() {
    const sharing = useDemand("$sharing")
    const [shareParam] = useSearchParam("share", null)
    const [query, setQuery, debouncedQuery] = useDebouncedValue("", 300)
    const [open, setOpen] = useState(false)
    const { pathname } = useLocation()
    const regimeId = getShareLinkSearchRegimeId(pathname)
    const regime = useTreeItem(regimeId)
    const placeholder = getShareLinkSearchPlaceholder(regime?.label)
    const matches = useRegimeSearchMatches(regime, debouncedQuery)

    if (!sharing && shareParam === null) return null
    if (!regime) return null

    return (
        <Autocomplete
            freeSolo
            disableClearable
            fullWidth
            sx={{ width: 1, maxWidth: 760, mx: "auto", px: 2 }}
            options={matches}
            filterOptions={showAllMatches}
            getOptionLabel={getMatchLabel}
            inputValue={query}
            onInputChange={setFromValueParam(setQuery)}
            onChange={onSelect}
            open={open && debouncedQuery.trim().length > 0}
            onOpen={openResults}
            onClose={closeResults}
            noOptionsText="No matches found"
            renderOption={renderSearchResult}
            renderInput={renderSearchInput}
        />
    )

    function showAllMatches(options) {
        return options
    }

    function getMatchLabel(option) {
        if (typeof option === "string") return option
        return option.code ? `${option.code} - ${option.label}` : option.label
    }

    function renderSearchResult(optionProps, option) {
        const { key, ...rest } = optionProps
        return <ShareLinkSearchResult key={key} props={rest} item={option} />
    }

    function renderSearchInput(params) {
        return (
            <TextField
                {...params}
                data-cy="share-link-search-input"
                variant="outlined"
                placeholder={placeholder}
                InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                        <InputAdornment position="end">
                            <Button
                                aria-label="search"
                                data-cy="share-link-search-button"
                                onClick={search}
                                size="small"
                                sx={{ fontSize: 24 }}
                                color="primary"
                                variant="contained"
                            >
                                <Iconify icon="mdi:search" />
                            </Button>
                        </InputAdornment>
                    ),
                }}
            />
        )
    }

    function onSelect(event, value) {
        if (!value) return
        if (typeof value === "string") search()
        else openResult(value)
    }

    function openResult(item) {
        if (item.type === SCHEDULE) {
            if (handleUnlicensedContent(item)) return
            navigate(getFacilityProfileScheduleRoute({ stats: { id: item.id }, schedule: item, share: shareParam }))
        } else {
            navigate(getRegimeRoute({ regimeId, nodeId: item.id, share: shareParam }))
        }
    }

    function search() {
        const searchQuery = clearSearchTerm(query)
        if (!searchQuery) return
        closeResults()
        SearchFor.raise(searchQuery)
    }

    function openResults() {
        setOpen(true)
    }

    function closeResults() {
        setOpen(false)
    }
}
